import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { BUSINESS } from "@/lib/site";
import { hideOnError, imgSrc } from "@/components/ImageSlot";
import { AccentTitle } from "@/components/AccentTitle";
import { TrustBar } from "@/components/home/TrustBar";

// First-screen hero: fills the viewport below the fixed header (.hero-fold) with
// the content centred and the trust bar pinned to the bottom edge, so all four
// trust points are visible on load. Photo is object-fit: cover under the overlay.
const HERO = {
  slotId: "hero-rear-extension-molesey.webp",
  alt: "Finished rear extension with bifold doors in Molesey by DMS Home Improvements",
};

export const Hero = () => {
  const reduce = useReducedMotion();
  // Load-in: fade + gentle rise, staggered. Reduced motion renders in place (no hidden state).
  const rise = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] },
  });
  return (
    <section
      data-testid="hero-section"
      aria-labelledby="hero-heading"
      className="hero-fold relative isolate flex flex-col overflow-hidden bg-navy"
    >
      {/* Photo area. On mobile it is the positioning box for the photo and overlays, so the
          photo ends where the trust block begins. On desktop (md:static) they position
          against the section as before. */}
      <div data-testid="hero-photo-area" className="relative flex min-h-0 flex-1 md:static">
        <div data-slot={HERO.slotId} className="absolute inset-0">
          <img
            src={imgSrc(HERO.slotId)}
            alt={HERO.alt}
            width="2000"
            height="1342"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            draggable={false}
            onError={hideOnError}
            data-testid="hero-image"
            className="h-full w-full object-cover"
          />
        </div>

        <div
          aria-hidden="true"
          data-testid="hero-overlay-desktop"
          className="absolute inset-0 hidden md:block"
          style={{ background: "linear-gradient(to top, rgba(18,38,58,0.72) 0%, rgba(18,38,58,0.25) 100%)" }}
        />
        <div
          aria-hidden="true"
          data-testid="hero-overlay-mobile"
          className="absolute inset-0 md:hidden"
          style={{ background: "linear-gradient(to top, rgba(18,38,58,0.92) 0%, rgba(18,38,58,0.7) 55%, rgba(18,38,58,0.5) 100%)" }}
        />

        <div className="hero-content container-site relative z-10 flex min-h-0 flex-1 items-center md:py-20">
          <div className="max-w-[640px]">
            <motion.h1
              {...rise(0)}
              id="hero-heading"
              data-testid="hero-heading"
              className="hero-h1 font-serif font-bold text-white md:text-[length:clamp(2.5rem,5vw,4rem)] md:leading-[1.05]"
            >
              Extensions, Lofts &amp; Plastering in <AccentTitle title="Molesey & Kingston" accent="Molesey & Kingston" />
            </motion.h1>
            <motion.p {...rise(0.1)} data-testid="hero-subline" className="hero-sub text-base text-white/90 md:mt-5 md:text-xl">
              Family-run builders and plasterers with 20+ years&rsquo; experience. Free site visits, fixed written quotes, no deposit.
            </motion.p>
            <motion.div {...rise(0.2)} className="hero-actions flex flex-col sm:flex-row md:mt-8 md:gap-3">
              <Link to="/contact" data-testid="hero-quote-button" className="btn btn-brand w-full sm:w-auto">
                Get My Free Quote
              </Link>
              <a href={BUSINESS.phoneHref} data-testid="hero-call-button" className="btn btn-outline-white w-full sm:w-auto">
                <Phone size={18} strokeWidth={2} aria-hidden="true" />
                Call {BUSINESS.founderFirstName}
              </a>
            </motion.div>
            <motion.p {...rise(0.28)} data-testid="hero-trust-strip" className="hero-strip text-sm font-medium text-white/85 md:mt-6">
              Fully insured · Free estimates · Fixed prices · 20+ years
            </motion.p>
          </div>
        </div>
      </div>

      <motion.div {...rise(0.36)} data-testid="hero-trust-box" className="relative z-10 shrink-0">
        <TrustBar />
      </motion.div>
    </section>
  );
};
